'use client';

import { forwardRef, type ReactNode } from 'react';
import { AnimatePresence, m } from 'motion/react';

import { useCollapsibleContext } from '@ark-ui/react/collapsible';
import { collapseVariants } from '@shaojiang/lib/motion';

// Root and Trigger are Park UI's as-is.
export * from '../styled/collapsible';

export interface ContentProps {
  children?: ReactNode;
  className?: string;
}

/**
 * Collapsible body that animates its height open and closed. Ark's own content element hides
 * itself immediately on close, so we render a Motion element with the same id instead (the
 * trigger's aria-controls still points at it) and let AnimatePresence play the exit.
 */
export const Content = forwardRef<HTMLDivElement, ContentProps>(({ children, className }, ref) => {
  const collapsible = useCollapsibleContext();
  const { id } = collapsible.getContentProps() as { id?: string };
  return (
    <AnimatePresence initial={false}>
      {collapsible.open && (
        <m.div
          key='content'
          ref={ref}
          id={id}
          className={className}
          variants={collapseVariants}
          initial='closed'
          animate='open'
          exit='closed'
        >
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
});
Content.displayName = 'CollapsibleContent';
